/**
 * The step between a what-if and the real forecast. A what-if is private to the person trying it;
 * saving it changes the forecast everyone sees, so the person confirms that first.
 */

import React, { useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import moment from "moment";
import { ProductDemandSettings } from "services/InventoryService";
import { formatNumber } from "../../utils";

interface SaveWhatIfDialogProps {
  /** Null when closed */
  productName: string | null;
  settings: ProductDemandSettings;
  /** Resolves once saved; rejects with the reason if it could not be */
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

const monthLabel = (monthKey: string) => moment(monthKey, "YYYY-MM").format("MMM YYYY");

export default function SaveWhatIfDialog({ productName, settings, onConfirm, onClose }: SaveWhatIfDialogProps): JSX.Element {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const close = () => {
    if (isSaving) return;
    setErrorMessage(null);
    onClose();
  };

  const confirm = async () => {
    setIsSaving(true);
    setErrorMessage(null);
    try {
      await onConfirm();
      onClose();
    } catch (error: any) {
      setErrorMessage(error?.message || String(error));
    } finally {
      setIsSaving(false);
    }
  };

  const multipliers = Object.entries(settings.salesModifiers).sort();
  const overrides = Object.entries(settings.manualOverrides).sort();

  return (
    <Dialog open={productName !== null} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Save this adjustment for everyone?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          So far this is a what-if that only you can see. Saving it makes it part of the team's real forecast
          for <strong>{productName}</strong>:
        </Typography>
        <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3, "& li": { mb: 0.5 } }}>
          <Typography component="li" variant="body2">Everyone who opens the Inventory tool sees it.</Typography>
          <Typography component="li" variant="body2">
            It drives this product's out-of-stock dates, recommended order dates and the out-of-stock alerts.
          </Typography>
          <Typography component="li" variant="body2">It stays until someone changes or clears it.</Typography>
        </Box>

        <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mb: 0.5 }}>What will be saved</Typography>
        {multipliers.length === 0 && overrides.length === 0 ? (
          <Typography variant="body2">No adjustments — this clears the ones saved for this product.</Typography>
        ) : (
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {multipliers.map(([monthKey, modifier]) => (
              <Typography component="li" variant="body2" key={`multiplier-${monthKey}`}>{monthLabel(monthKey)}: projected sales × {modifier}</Typography>
            ))}
            {overrides.map(([monthKey, units]) => (
              <Typography component="li" variant="body2" key={`override-${monthKey}`}>{monthLabel(monthKey)}: projected sales set to {formatNumber(units)} units</Typography>
            ))}
          </Box>
        )}

        {errorMessage && <Alert severity="error" sx={{ mt: 2 }}>Not saved: {errorMessage}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={isSaving}>Keep as a what-if</Button>
        <Button variant="contained" onClick={confirm} disabled={isSaving} startIcon={isSaving ? <CircularProgress size={16} /> : undefined}>
          Save for everyone
        </Button>
      </DialogActions>
    </Dialog>
  );
}
